// Per-client Quality Management — the client's slice of the four Quality
// Mgmt pages: satisfaction surveys, the complaints log, incidents recorded
// about them, and a period report they can be exported from.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, startOfYear } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { getVisitsByDateRange } from '../../lib/axiscare';
import { matchesClient, normalizeVisits } from '../../hooks/useVisits';
import ClientComplaintsTab from './ClientComplaintsTab';
import IncidentReportModal from '../quality/IncidentReportModal';

const card = { background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '6px 8px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '6px 8px', borderBottom: '0.5px solid var(--border)', fontSize: 12.5, verticalAlign: 'top' };
const d = (x) => {
  if (!x) return '—';
  const dt = x instanceof Date ? x : parseISO(String(x).length > 10 ? x : `${x}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? '—' : format(dt, 'd MMM yyyy');
};
const iso = (x) => format(x, 'yyyy-MM-dd');

export default function ClientQmTab({ client, view }) {
  if (view === 'complaints') return <ClientComplaintsTab client={client} />;
  if (view === 'incidents') return <Incidents client={client} />;
  if (view === 'reports') return <ClientReport client={client} />;
  return <Surveys client={client} />;
}

// ── Satisfaction surveys ────────────────────────────────────────────────────
function Surveys({ client }) {
  const [rows, setRows] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      const [s, r] = await Promise.all([
        supabase.from('qa_surveys').select('id, period_label, survey_month, survey_year, status, sent_at, completed_at, guardian_name, guardian_email')
          .eq('client_id', client.id).order('survey_year', { ascending: false }).order('survey_month', { ascending: false }),
        supabase.from('qa_responses').select('*').eq('client_id', client.id),
      ]);
      if (cancel) return;
      setRows(s.data || []);
      setResponses(Object.fromEntries((r.data || []).map(x => [x.qa_survey_id, x])));
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [client.id]);

  const done = rows.filter(x => x.status === 'completed');
  const avg = useMemo(() => {
    const vals = done.map(x => responses[x.id]?.avg_score).filter(v => v != null).map(Number);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : null;
  }, [done, responses]);
  const flagged = done.filter(x => responses[x.id]?.flagged_for_followup).length;

  if (loading) return <p style={{ color: 'var(--text3)' }}>Loading…</p>;
  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Satisfaction surveys</h3>
          <Link to="/qa" className="btn btn-outline btn-sm">QA Surveys</Link>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text2)' }}>
          <span><b>{rows.length}</b> sent</span>
          <span><b>{done.length}</b> completed</span>
          {avg && <span><b>{avg}</b> average score</span>}
          {flagged > 0 && <span style={{ color: 'var(--warn)' }}><b>{flagged}</b> flagged for follow-up</span>}
        </div>
      </div>
      <div style={card}>
        {rows.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>No surveys sent to this family yet.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Period', 'Sent to', 'Status', 'Score', 'What is going well', 'What could improve'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map(s => {
                  const r = responses[s.id];
                  return (
                    <tr key={s.id} style={r?.flagged_for_followup ? { background: 'var(--warn-bg)' } : undefined}>
                      <td style={td}>{s.period_label || `${s.survey_month}/${s.survey_year}`}<div style={{ fontSize: 10.5, color: 'var(--text3)' }}>sent {d(s.sent_at)}</div></td>
                      <td style={td}>{s.guardian_name || '—'}<div style={{ fontSize: 10.5, color: 'var(--text3)' }}>{s.guardian_email}</div></td>
                      <td style={td}>{s.status === 'completed'
                        ? <span style={{ color: 'var(--success)' }}>Completed {d(s.completed_at)}</span>
                        : <span style={{ color: 'var(--text3)' }}>Awaiting reply</span>}</td>
                      <td style={td}>{r?.avg_score != null ? <b>{Number(r.avg_score).toFixed(1)}</b> : '—'}{r?.answer_recommend ? <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>recommends: {r.answer_recommend}</div> : null}</td>
                      <td style={td}>{r?.feedback_going_well || '—'}</td>
                      <td style={td}>{r?.feedback_improve || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Incidents recorded about this client ────────────────────────────────────
function Incidents({ client }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let cancel = false;
    supabase.from('staff_incidents')
      .select('id, reference_no, incident_date, incident_type, category, description, action_taken, status, reportable, reported_external, reported_external_at, follow_up_date, reported_by, staff:staff ( first_name, last_name )')
      .eq('client_id', client.id).neq('status', 'void').order('incident_date', { ascending: false })
      .then(({ data }) => { if (!cancel) { setRows(data || []); setLoading(false); } });
    return () => { cancel = true; };
  }, [client.id, nonce]);

  const open = rows.filter(r => r.status !== 'closed' && r.status !== 'resolved').length;
  const unreported = rows.filter(r => r.reportable && !r.reported_external).length;
  if (loading) return <p style={{ color: 'var(--text3)' }}>Loading…</p>;
  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Incidents</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setReporting(true)}>Report an incident</button>
            <Link to="/hr/disciplinary-log" className="btn btn-outline btn-sm">Incident Log</Link>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--text2)' }}>
          <span><b>{rows.length}</b> recorded</span>
          {open > 0 && <span style={{ color: 'var(--warn)' }}><b>{open}</b> open</span>}
          {unreported > 0 && <span style={{ color: 'var(--danger)' }}><b>{unreported}</b> reportable, not yet reported externally</span>}
        </div>
      </div>
      <div style={card}>
        {rows.length === 0 ? <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: 0 }}>No incidents recorded for this client.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Date', 'Type', 'What happened', 'Action taken', 'Staff involved', 'Status'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} style={r.reportable && !r.reported_external ? { background: 'var(--danger-bg)' } : undefined}>
                    <td style={td}>{d(r.incident_date)}<div style={{ fontSize: 10.5, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{r.reference_no || ''}</div></td>
                    <td style={td}>{(r.incident_type || '').replace(/_/g, ' ')}{r.reportable ? <div style={{ fontSize: 10.5, color: r.reported_external ? 'var(--success)' : 'var(--danger)' }}>{r.reported_external ? `reported ${d(r.reported_external_at)}` : 'reportable'}</div> : null}</td>
                    <td style={td}>{r.description}</td>
                    <td style={td}>{r.action_taken || '—'}{r.follow_up_date ? <div style={{ fontSize: 10.5, color: 'var(--text3)' }}>follow-up {d(r.follow_up_date)}</div> : null}</td>
                    <td style={td}>{r.staff ? `${r.staff.last_name}, ${r.staff.first_name}` : '—'}</td>
                    <td style={td}>{(r.status || '').replace(/_/g, ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {reporting && (
        <IncidentReportModal client={client} subject="client" onClose={() => setReporting(false)} onFiled={() => setNonce(n => n + 1)} />
      )}
    </div>
  );
}

// ── Period report for one client, exportable ────────────────────────────────
const PERIODS = [
  ['this_month', 'This month', () => [startOfMonth(new Date()), endOfMonth(new Date())]],
  ['last_month', 'Last month', () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))]],
  ['last_3', 'Last 3 months', () => [startOfMonth(subMonths(new Date(), 2)), endOfMonth(new Date())]],
  ['this_year', 'This year', () => [startOfYear(new Date()), endOfMonth(new Date())]],
];

function ClientReport({ client }) {
  const [periodKey, setPeriodKey] = useState('this_month');
  const [visits, setVisits] = useState([]);
  const [extras, setExtras] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [from, to] = useMemo(() => (PERIODS.find(p => p[0] === periodKey)[2])(), [periodKey]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    const [vres, comp, inc, sup, plan] = await Promise.all([
      getVisitsByDateRange(iso(from), iso(to)),
      supabase.from('client_complaints').select('id, status').eq('client_id', client.id).gte('date_received', iso(from)).lte('date_received', iso(to)),
      supabase.from('staff_incidents').select('id, status').eq('client_id', client.id).gte('incident_date', iso(from)).lte('incident_date', iso(to)),
      supabase.from('supervisor_visits').select('id, visit_date, subject_kind').eq('client_id', client.id).eq('status', 'complete').gte('visit_date', iso(from)).lte('visit_date', iso(to)),
      supabase.from('care_plans').select('version_number, effective_date, review_due').eq('client_id', client.id).eq('status', 'active').maybeSingle(),
    ]);
    if (vres.error) setError('AxisCare unavailable — visit figures may be incomplete.');
    const mine = normalizeVisits(vres.data?.results?.visits).filter(v => matchesClient(v, client));
    setVisits(mine);
    setExtras({ complaints: comp.data || [], incidents: inc.data || [], supervision: sup.data || [], plan: plan.data || null });
    setLoading(false);
  }, [client, from, to]);
  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => {
    const active = visits.filter(v => v._status !== 'Missed');
    return {
      visits: active.length,
      hours: active.reduce((s, v) => s + (v._hours || 0), 0),
      completed: active.filter(v => v.clockIn).length,
      missed: visits.filter(v => v._status === 'Missed').length,
    };
  }, [visits]);

  const download = () => {
    const header = ['Date', 'Start', 'End', 'Hours', 'Service', 'Caregiver', 'Status'];
    const rows = visits.map(v => {
      const s = v.scheduledStartDate || v.startDate, e = v.scheduledEndDate || v.endDate;
      return [
        s ? format(parseISO(s), 'yyyy-MM-dd') : '', s ? format(parseISO(s), 'HH:mm') : '', e ? format(parseISO(e), 'HH:mm') : '',
        (v._hours || 0).toFixed(2), v.service?.description || v.service?.code || '',
        v.caregiver ? `${v.caregiver.lastName || ''}, ${v.caregiver.firstName || ''}`.replace(/^, |, $/, '') : 'Unassigned',
        v._status,
      ];
    });
    const csv = [header, ...rows].map(r => r.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = `${client.client_id}_${iso(from)}_to_${iso(to)}.csv`;
    a.click();
  };

  const line = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '0.5px solid var(--border)', fontSize: 13 }}>
      <span style={{ color: 'var(--text3)' }}>{label}</span><span>{value}</span>
    </div>
  );

  return (
    <div>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Client report</h3>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {PERIODS.map(([k, label]) => (
              <button key={k} onClick={() => setPeriodKey(k)} style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: periodKey === k ? 'var(--accent)' : 'transparent', color: periodKey === k ? 'var(--white)' : 'var(--text3)', borderColor: periodKey === k ? 'var(--accent)' : 'var(--border)' }}>{label}</button>
            ))}
            <button className="btn btn-outline btn-sm" disabled={loading || visits.length === 0} onClick={download}>↓ Visits CSV</button>
            <Link to="/reports" className="btn btn-outline btn-sm">Reports &amp; Export</Link>
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{d(from)} – {d(to)}</div>
        {error && <div style={{ background: 'var(--warn-bg)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--warn)', marginTop: 10 }}>{error}</div>}
      </div>

      {loading ? <p style={{ color: 'var(--text3)' }}>Loading…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <div style={card}>
            <h3 style={{ marginTop: 0, fontSize: 14 }}>Service delivered</h3>
            {line('Visits', stats.visits)}
            {line('Hours', stats.hours.toFixed(1))}
            {line('Completed (clocked)', stats.completed)}
            {line('Missed', stats.missed)}
            {line('Authorised per week', `${(Number(client.pa_skilled_hours_week) || 0) + (Number(client.pa_pss_hours_week) || 0)}h`)}
          </div>
          <div style={card}>
            <h3 style={{ marginTop: 0, fontSize: 14 }}>Oversight</h3>
            {line('Supervisory visits completed', extras.supervision?.length ?? 0)}
            {line('Last assessment', d(client.nurse_assessment_date))}
            {line('Care plan', extras.plan ? `v${extras.plan.version_number} · review ${d(extras.plan.review_due)}` : 'none active')}
            {line('PA expires', d(client.pa_expiry_date))}
          </div>
          <div style={card}>
            <h3 style={{ marginTop: 0, fontSize: 14 }}>Quality</h3>
            {line('Complaints raised', extras.complaints?.length ?? 0)}
            {line('Complaints still open', (extras.complaints || []).filter(c => c.status !== 'closed' && c.status !== 'resolved').length)}
            {line('Incidents', extras.incidents?.length ?? 0)}
          </div>
        </div>
      )}
    </div>
  );
}
